import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import {

  GetMaintenanceContractsResponse,
  GetAncillaryCostResponse,
  InHouseMaintenanceResponse,
  KpiInsightsGraphResponse
} from '../models/maintenance-costing-response.model';
import { Response } from '../../../../_shared/models/response.model';
import { PaginatedResult } from '../../../../_shared/models/paginated-result.model';
import { HttpClient } from '@angular/common/http';
import { AddAncillaryCostRequest, AddContractRequest, MaintenanceCostingRequest, UploadTempInvoiceRequest } from '../models/maintenance-costing-request.model';
import { environment } from '../../../../../environments/environment';
import { SummaryRequest } from '../../../../_shared/models';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceCostingService {
  private httpClient = inject(HttpClient);

 async getMaintenanceCostingContractRequest(request: MaintenanceCostingRequest, cancelSubject: Subject<void>):
    Promise<Response<PaginatedResult<GetMaintenanceContractsResponse>>> {
    return await firstValueFrom(
      this.httpClient.post<Response<PaginatedResult<GetMaintenanceContractsResponse>>>(`${environment.apiEndpoint}/MaintenanceCosting/maintenance-contracts/filter`, request)
        .pipe(takeUntil(cancelSubject))
    );
  }

  async getAncillaryCostRequest(request: MaintenanceCostingRequest, cancelSubject: Subject<void>):
    Promise<Response<PaginatedResult<GetAncillaryCostResponse>>>{
      return await firstValueFrom(
        this.httpClient.post<Response<PaginatedResult<GetAncillaryCostResponse>>>(`${environment.apiEndpoint}/MaintenanceCosting/ancillary-costs/filter`,request)
          .pipe(takeUntil(cancelSubject))
      );
    }

  async downloadInvoiceRequest(ancillaryCostId:number, cancelSubject: Subject<void>):
    Promise<Response<string>>{
    return await firstValueFrom(
      this.httpClient.get<Response<string>>(`${environment.apiEndpoint}/MaintenanceCosting/ancillary-costs/${ancillaryCostId}/invoice/download`)
        .pipe(takeUntil(cancelSubject))
    );
  }

  async AddAncillaryCostRequest(request: AddAncillaryCostRequest, cancelSubject: Subject<void>):
  Promise<Response<any>>{
    return await firstValueFrom(this.httpClient.post(`${environment.apiEndpoint}/MaintenanceCosting/add-ancillary-cost`,request).pipe(
      takeUntil(cancelSubject))
    );
  }

  async AddContractRequest(request: AddContractRequest, cancelSubject: Subject<void>):
    Promise<Response<any>>{
      return await firstValueFrom(this.httpClient.post(
        `${environment.apiEndpoint}/MaintenanceCosting/add-contract`,request)
          .pipe(takeUntil(cancelSubject))
        );
  }

  async UploadTempRequest(request: UploadTempInvoiceRequest, cancelSubject: Subject<void>):
    Promise<Response<void>>{
      const formData = new FormData();
      formData.append('AssetId', request.assetId.toString());
      formData.append('Reference', request.reference);
      formData.append('Extension', request.extension);
      formData.append('MediaType', request.mediaType);
      formData.append('Invoice', request.invoice, request.invoice.name);

      // The endpoint is multipart/form-data, so the Content-Type header is left to the browser to set with its boundary
      return await firstValueFrom(this.httpClient.post<Response<void>>(`${environment.apiEndpoint}/MaintenanceCosting/ancillary-costs/invoice/temp`, formData)
      .pipe(takeUntil(cancelSubject))
    );
  }

  async GetInHouseMaintenance(request: MaintenanceCostingRequest, cancelSubject: Subject<void>):
  Promise<Response<PaginatedResult<InHouseMaintenanceResponse>>>{
    return await firstValueFrom(
        this.httpClient.post<Response<PaginatedResult<InHouseMaintenanceResponse>>>(`${environment.apiEndpoint}/MaintenanceCosting/in-house-maintenance/filter`,request)
          .pipe(takeUntil(cancelSubject))
      );
  }

  async MonthlyInHouseCostKpi(request: SummaryRequest, cancelSubject: Subject<void>):
  Promise<Response<KpiInsightsGraphResponse>>{
    return await firstValueFrom(
      this.httpClient.post<Response<KpiInsightsGraphResponse>>(`${environment.apiEndpoint}/MaintenanceCostingInsights/in-house-cost`,request)
        .pipe(takeUntil(cancelSubject))
    );
  }

  async MonthlyOutsourceKpi(request: SummaryRequest, cancelSubject: Subject<void>):
  Promise<Response<KpiInsightsGraphResponse>>{
    return await firstValueFrom(
      this.httpClient.post<Response<KpiInsightsGraphResponse>>(`${environment.apiEndpoint}/MaintenanceCostingInsights/outsourced-cost`,request)
        .pipe(takeUntil(cancelSubject))
    );
  }

  async InHouseCostPerKm(request: SummaryRequest, cancelSubject: Subject<void>):
  Promise<Response<KpiInsightsGraphResponse>>{
    return await firstValueFrom(
      this.httpClient.post<Response<KpiInsightsGraphResponse>>(`${environment.apiEndpoint}/MaintenanceCostingInsights/in-house-cost-per-km`,request)
        .pipe(takeUntil(cancelSubject))
    );
  }

  async OutSourceCostPerKm(request: SummaryRequest, cancelSubject: Subject<void>):
  Promise<Response<KpiInsightsGraphResponse>>{
    return await firstValueFrom(
      this.httpClient.post<Response<KpiInsightsGraphResponse>>(`${environment.apiEndpoint}/MaintenanceCostingInsights/outsourced-cost-per-km`,request)
        .pipe(takeUntil(cancelSubject))
    );
  }

  async CostContractByTypeFML(request: SummaryRequest, cancelSubject: Subject<void>):
  Promise<Response<KpiInsightsGraphResponse>>{
    return await firstValueFrom(
      this.httpClient.post<Response<KpiInsightsGraphResponse>>(`${environment.apiEndpoint}/MaintenanceCostingInsights/full-maintenance-lease-cost`,request)
        .pipe(takeUntil(cancelSubject))
    );
  }

  async CostContractByTypeFMC(request: SummaryRequest, cancelSubject: Subject<void>):
  Promise<Response<KpiInsightsGraphResponse>>{
    return await firstValueFrom(
      this.httpClient.post<Response<KpiInsightsGraphResponse>>(`${environment.apiEndpoint}/MaintenanceCostingInsights/full-maintenance-contract-cost`,request)
        .pipe(takeUntil(cancelSubject))
    );
  }

  async CostContractByTypeServiceOnly(request: SummaryRequest, cancelSubject: Subject<void>):
  Promise<Response<KpiInsightsGraphResponse>>{
    return await firstValueFrom(
      this.httpClient.post<Response<KpiInsightsGraphResponse>>(`${environment.apiEndpoint}/MaintenanceCostingInsights/service-only-cost`,request)
        .pipe(takeUntil(cancelSubject))
    );
  }
}